<script>
    import { LayerCake, Svg, Html } from "layercake";
    import { format } from "d3-format";
    import { ascending, extent, sum } from "d3-array";
    import { timeParse, timeFormat } from "d3-time-format";
    import { onMount } from "svelte";
    import { addCommas, titleCase, the, formatRemainsOf } from "./helpers.js";

    import BarStacked from "../_components/BarStacked.svelte";
    import ProgressLine from "../_components/ProgressLine.svelte";
    import AxisX from "../_components/AxisX.svelte";
    import AxisY from "../_components/AxisY.svelte";
    import Tooltip from "../_components/Tooltip.svelte";
    // @ts-ignore
    import guff from "dataset:../_data/guff.aml";

    let height;
    let width;
    let yMax = 1;

    /**
     * @typedef {object} Notice
     * @prop {string} notice_id
     * @prop {string} [main_institution] on homepage and institution detail
     * @prop {string} [tribe] on tribe detail
     * @prop {string} [main_institution_short_name] on tribe detail
     * @prop {string} completed_type
     * @prop {string | Date} publication_date transformed from string to date in this
     *     component
     * @prop {string} link
     * @prop {string} counties
     * @prop {string} county_states
     * @prop {string | number} number_mni gets processed from string to number
     * @prop {number} year gets calculate
     * @prop {number} [cumulative_mni] calculated in this component
     * @prop {number} [pct_complete] calculated in this component
     * @prop {number} [cumulative_pct_complete] calculated in this component
     */

    /**
     * @type {Notice[]}
     */
    let notices = [];
    var closeToTop = false;

    /** @type { import("../scripts/charts.js").PageContext } */
    export let ctx;

    /** @type {number} */
    let totalRemainsAll;

    /** @type {CustomEvent} */
    let event;
    // Should get overridden by binding from BarStacked when rendered.
    let hideTooltip = true;

    var parseDate = timeParse("%Y-%m-%d");
    var getYear = timeFormat("%Y");
    const padding = {
        top: 15,
        bottom: 20,
        left: ctx["type"] == "homepage" ? 0 : 30,
        right: 0,
    };
    const xKey = "year";
    const yKey =
        ctx["type"] == "tribe" ? "cumulative_mni" : "cumulative_pct_complete";
    const lastYear = 2025; //TODO: check if this needs to be updated when you update the data

    var formatTickY = format(",");
    if (ctx["type"] != "tribe") formatTickY = (d) => format(".0%")(d);

    /**
     * @param {string} og_county_states
     */
    function getCountyState(og_county_states) {
        let hasZUnknown = og_county_states.search("ZUNKNOWN");
        let county_states = og_county_states
            .replaceAll(" ||| ZUNKNOWN,ZUNKNOWN","")
            .replaceAll("ZUNKNOWN,", "");
        let county_states_for_count = og_county_states
            .replaceAll(" ||| ZUNKNOWN,ZUNKNOWN","")
            .replaceAll("||| ZUNKNOWN,", "");

        let num_other_counties = 0;
        if (county_states_for_count.indexOf("|") >= 0) {
            num_other_counties =
                county_states_for_count.split(" ||| ").length - 1;
        }
        let first_county;
        if (num_other_counties == 0) {
            first_county = titleCase(county_states.replace(",", ", ")).replace(
                "|||",
                "and an unknown county in "
            );
            return first_county;
        } else {
            first_county = county_states
                .substring(0, county_states.indexOf("|"))
                .replace(",", ", ");
            return (
                titleCase(first_county) +
                " and " +
                (hasZUnknown != -1 ? "at least " : "") +
                (num_other_counties == 1
                    ? "one other county"
                    : addCommas(num_other_counties) + " other counties")
            );
        }
    }
    let summaryData;

    onMount(async () => {
        // Load notice datasets
        if (ctx["type"] == "homepage") {
            ({ default: summaryData } = await import(
                // @ts-expect-error
                "dataset:../_data/summary/progress_overall.csv"
            ));
            totalRemainsAll = sum(summaryData, (d) => d.number_mni);

            ({ default: notices } = await import(
                // @ts-expect-error
                "dataset:../_data/notices/byYearSummary.js"
            ));
        } else if (ctx["type"] == "institution" || ctx["type"] == "tribe") {
            if (ctx["type"] == "institution") {
                totalRemainsAll = +JSON.parse(
                    document.getElementById("mni_total").textContent
                )[0];
            }

            // load area chart data
            notices = JSON.parse(
                // @ts-ignore: assume element with this ID exists on detail page
                document.getElementById("noticeData").textContent
            );
        }

        // process datasets
        notices.forEach((n) => {
            n["publication_date"] = /** @type {Date} */ (
                parseDate(/** @type {string} */ (n["publication_date"]))
            );
            n["year"] = +getYear(/** @type {Date} */ (n.publication_date));
            n["number_mni"] = +n.number_mni;
        });

        notices.sort((a, b) =>
            ascending(
                new Date(a.publication_date),
                new Date(b.publication_date)
            )
        );

        for (let i = 0; i < notices.length; i++) {
            if (i == 0) {
                notices[i].cumulative_mni = +notices[i].number_mni;
            } else {
                notices[i].cumulative_mni =
                    +(/** @type {number} */ (notices[i - 1].cumulative_mni)) +
                    +notices[i].number_mni;
            }
            notices[i].pct_complete =
                /** @type {number} */ (notices[i].number_mni) / totalRemainsAll;
            if (i == 0) {
                notices[i].cumulative_pct_complete = notices[i].pct_complete;
            } else {
                notices[i].cumulative_pct_complete =
                    /** @type {number} */ (
                        notices[i - 1].cumulative_pct_complete
                    ) + /** @type {number} */ (notices[i].pct_complete);
            }
        }
        yMax =
            ctx["type"] != "tribe"
                ? yMax
                : /** @type {number} */ (extent(notices, (d) => d[yKey])[1]) *
                  1.2;

        closeToTop =
            /** @type {number} */ (
                notices[notices.length - 1]?.cumulative_pct_complete
            ) > 0.9;
    });
</script>

<div
    class="chart-container"
    class:close-to-top={closeToTop}
    bind:clientHeight={height}
    bind:clientWidth={width}
>
    <LayerCake
        {padding}
        x={xKey}
        y={yKey}
        ssr={true}
        xDomain={[1990, lastYear]}
        yDomain={[0, yMax]}
        data={notices}
    >
        <Svg>
            <AxisX ticks={ctx["type"] == "homepage" ? [1990, 2010, 2024] : [1990, 2000, 2010, 2020, 2024]} {ctx} />
            {#if ctx["type"] == "institution"}
                <AxisY
                    formatTick={formatTickY}
                    ticks={[0, 0.25, 0.5, 0.75, 1]}
                />
            {:else if ctx["type"] == "tribe"}
                <AxisY formatTick={formatTickY} ticks={3} />
            {/if}
            {#if ctx["type"] != "homepage"}
                <BarStacked
                    {ctx}
                    {lastYear}
                    on:message={(evt) => (event = evt)}
                    bind:hideTooltip
                />
            {/if}
            <ProgressLine {ctx} {yKey} formatY={formatTickY} {lastYear} />
        </Svg>

        <Html pointerEvents={false}>
            {#if ctx["type"] != "homepage"}
                <Tooltip evt={event} {hideTooltip} let:detail>
                    <!-- For the tooltip, do another data join because the hover event only has the data from the geography data -->
                    {@const tooltipData = {
                        ...detail.props,
                    }}

                    <div class="tooltip-content">
                        <!-- {tooltipData.county_states}
                        {tooltipData.county_states.search("ZUNKNOWN,ZUNKNOWN") != 0} -->
                        {#if ctx["type"] == "institution"}
                            <!-- INSTITUTION PAGE -->
                            {guff.inst_progress_tooltip1a}
                            {tooltipData.year}, {guff.inst_progress_tooltip1b}
                            {#if tooltipData.number_mni == 1}
                                {#if tooltipData.county_states.search("ZUNKNOWN,ZUNKNOWN") != 0}
                                    {@html formatRemainsOf(
                                        tooltipData.number_mni,
                                        false,
                                        false,
                                        true
                                    )}
                                    {guff.inst_progress_tooltip1d}
                                    {titleCase(
                                        tooltipData.county_states
                                            .replaceAll(",", ", ")
                                            .replaceAll(" ||| ", "; ")
                                    )}.
                                {:else}
                                    {@html formatRemainsOf(
                                        tooltipData.number_mni,
                                        false,
                                        false,
                                        true
                                    )}.
                                {/if}
                            {:else if tooltipData.county_states.search("ZUNKNOWN,ZUNKNOWN") != 0}
                                {@html formatRemainsOf(
                                    tooltipData.number_mni,
                                    false,
                                    false,
                                    true
                                )}
                                {guff.inst_progress_tooltip1d}
                                {@html getCountyState(
                                    tooltipData.county_states
                                )}.
                            {:else}
                                {@html formatRemainsOf(
                                    tooltipData.number_mni,
                                    false,
                                    false,
                                    true
                                )}.
                            {/if}
                        {:else if ctx["type"] == "tribe"}
                            <!-- TRIBE PAGE -->
                            {guff.inst_progress_tooltip1a}
                            {tooltipData.year}, {the(
                                tooltipData.main_institution_short_name
                            )}
                            {guff.inst_progress_tooltip1b2}
                            {document.getElementById("tribe_noun").textContent} the
                            {#if tooltipData.number_mni == 1}
                                <!-- returned one remain -->
                                {#if tooltipData.county_states.search("ZUNKNOWN,ZUNKNOWN") != 0}
                                    {@html formatRemainsOf(
                                        tooltipData.number_mni,
                                        false,
                                        false,
                                        true
                                    )}
                                    {guff.inst_progress_tooltip1d}
                                    {@html getCountyState(
                                        tooltipData.county_states
                                            .replaceAll(",", ", ")
                                            .replaceAll(" ||| ", "; ")
                                    )}.
                                {:else}
                                    {@html formatRemainsOf(
                                        tooltipData.number_mni,
                                        false,
                                        false,
                                        true
                                    )}
                                {/if}
                            {:else if tooltipData.county_states.search("ZUNKNOWN,ZUNKNOWN") != 0}
                                <!-- returned more than one remain. none with totally unknown origins-->
                                {@html formatRemainsOf(
                                    tooltipData.number_mni,
                                    false,
                                    false,
                                    true
                                )}
                                {guff.inst_progress_tooltip1d}
                                {@html getCountyState(
                                    tooltipData.county_states
                                )}.
                            {:else}
                                <!-- returned more than one remain. some with totally unknown origins-->
                                {@html formatRemainsOf(
                                    tooltipData.number_mni,
                                    false,
                                    false,
                                    true
                                )}.
                            {/if}
                        {/if}
                    </div>
                </Tooltip>
            {/if}
        </Html>
    </LayerCake>
</div>

<style lang="scss">
    /*
      The wrapper div needs to have an explicit width and height in CSS.
      It can also be a flexbox child or CSS grid element.
      The point being it needs dimensions since the <LayerCake> element will
      expand to fill it.
    */
    .close-to-top {
        margin-top: var(--spacing2);
    }

    .chart-container {
        width: 100%;
        height: 100%;
    }

    .tooltip-content {
        padding: var(--spacing-1);
    }
</style>
